import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FieldError } from 'react-hook-form';
import { Info, Check, AlertCircle } from 'lucide-react';
import { Visitor } from '../../../../types/form-data.types';
import type { VisitStepFieldsConfig } from '../../../../hooks/use-ui-metadata-field-config.hook';
import { VisitorTemplateDownloadButton } from './visitor-template-download-button';
import { VisitorCsvUploadButton } from './visitor-csv-upload-button';
import { Tip } from 'components/shared/tip';
import { MAX_VISITORS_PER_UPLOAD } from '../../constants';

export interface CsvUploadState {
  fileName: string | null;
  visitorCount: number | null;
  error: string | null;
  validationError: string | null;
}

interface CsvUploadSectionProps {
  onUploadSuccess: (visitors: Visitor[]) => void;
  fieldsConfig: VisitStepFieldsConfig;
  error?: FieldError;
}

export const VisitorCsvUploadSection = ({
  onUploadSuccess,
  fieldsConfig,
  error,
}: CsvUploadSectionProps): JSX.Element => {
  const { formatMessage } = useIntl();

  const [uploadState, setUploadState] = useState<CsvUploadState>({
    fileName: null,
    visitorCount: null,
    error: null,
    validationError: null,
  });

  const handleUploadStateChange = useCallback((state: CsvUploadState) => {
    setUploadState(state);
  }, []);

  return (
    <div className="space-y-4 pt-4 border-t">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-lg font-medium">
            {formatMessage({
              id: 'createVisitFormV2.visitStep.uploadVisitorList',
              defaultMessage: 'Upload visitor list',
            })}
          </h3>
          <Tip
            content={formatMessage({
              id: 'createVisitFormV2.visitStep.uploadVisitorListTooltip',
              defaultMessage: 'Use our CSV template to add multiple visitors at once with their contact information',
            })}
          >
            <Info className="h-4 w-4 text-gray-400" />
          </Tip>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {formatMessage({
            id: 'createVisitFormV2.visitStep.uploadDescription',
            defaultMessage: 'Download our template, fill in your visitor details, and upload the completed file',
          })}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <VisitorTemplateDownloadButton />
        <VisitorCsvUploadButton
          onSuccess={onUploadSuccess}
          onStateChange={handleUploadStateChange}
          fieldsConfig={fieldsConfig}
        />
      </div>

      <div className="min-h-[20px] space-y-2">
        {/* Success message */}
        {uploadState.visitorCount !== null && !uploadState.error && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <Check className="h-4 w-4" />
            <span>
              {formatMessage(
                {
                  id: 'createVisitFormV2.visitStep.guestsFound',
                  defaultMessage: '{count} {count, plural, one {guest} other {guests}} found in your file',
                },
                { count: uploadState.visitorCount },
              )}
            </span>
          </div>
        )}

        {/* Validation error warning - show if CSV has invalid rows */}
        {uploadState.validationError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>
              {(() => {
                // Translate field keys to localized names
                const fieldKeys = uploadState.validationError.split(',');
                const translatedFields = fieldKeys.map((key) =>
                  formatMessage({ id: key, defaultMessage: key.split('.').pop() || key }),
                );
                return formatMessage(
                  {
                    id: 'createVisitFormV2.visitStep.visitorListMissingData',
                    defaultMessage: 'Visitor list is missing required data ({requiredFields})',
                  },
                  { requiredFields: translatedFields.join(', ') },
                );
              })()}
            </span>
          </div>
        )}

        {/* Form validation error - show when no CSV uploaded */}
        {error && !uploadState.visitorCount && !uploadState.error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>
              {formatMessage({
                id: error.message || 'errors.visitorCSVFileRequired',
                defaultMessage: 'Please upload a visitor list CSV file',
              })}
            </span>
          </div>
        )}

        {/* Upload error */}
        {uploadState.error && (
          <div className="text-sm text-destructive">
            {formatMessage(
              { id: uploadState.error, defaultMessage: 'Error uploading file. Please try again.' },
              { max: MAX_VISITORS_PER_UPLOAD },
            )}
          </div>
        )}
      </div>
    </div>
  );
};
